import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SlideTabs } from './SlideTabs'
import { RichTextV2 } from './RichTextV2'
import { ImagePanel } from './ImagePanel'
import { VideoPanel } from './VideoPanel'
import { ExtraElements } from './ExtraElements'
import { V2FieldControl } from './V2FieldControl'
import { getAtPath } from '../../utils/configPaths'
import classes from './V2Form.module.css'

export function order(field) {
  return typeof field.ui?.order === 'number' ? field.ui.order : Number.MAX_SAFE_INTEGER
}

// AD-253: the button is set once for the whole ad (in the Brand card), not per slide.
export function isButtonField(field) {
  return field.path.startsWith('cta.') && !field.ui?.hidden && !field.ui?.element
}

/**
 * Figma "Content" card (279:31602): slide tabs, then for the selected slide the Slogan
 * editor, the Image panel, the Extra Elements list and the "Delete Slide" confirmation row.
 */
export function ContentCard({ title, section, collection, shared, errors, selectedIndex, onSelect, onAdd, onRemove, onReorder }) {
  const { t } = useTranslation()
  const { configuration } = shared
  const [openElement, setOpenElement] = useState(null)
  const [elementModes, setElementModes] = useState({})
  const ui = section.ui || {}
  const items = getAtPath(configuration, collection.path, []) || []
  const index = Math.min(Math.max(0, selectedIndex || 0), Math.max(items.length - 1, 0))
  const item = items[index] || {}
  const basePath = `${collection.path}.${index}`
  const identityPath = collection.identityPath || 'id'
  const itemId = getAtPath(item, identityPath) || `slide-${index}`
  const fields = (collection.itemSections || []).flatMap((itemSection) => itemSection.fields || [])
  const main = fields.filter((field) => !field.ui?.hidden && !field.ui?.element && !isButtonField(field)).sort((a, b) => order(a) - order(b))
  const canAdd = items.length < (collection.maxItems || Infinity)
  const canRemove = items.length > (collection.minItems || 1)

  const render = (field) => {
    const path = `${basePath}.${field.path}`
    const props = {
      field,
      path,
      value: getAtPath(item, field.path),
      error: errors[path],
      onChange: (next) => shared.updateValue(path, next),
      configuration,
      item,
      templateManifest: shared.templateManifest,
      addAsset: shared.addAsset,
      addRenditionPreview: shared.addRenditionPreview,
      assetUrls: shared.assetUrls,
      assetSourceUrls: shared.assetSourceUrls,
    }
    if (field.type === 'rich-text') return <RichTextV2 key={`${itemId}:${field.path}`} {...props} sectionUi={ui} />
    if (field.type === 'image-editor') return <ImagePanel key={`${itemId}:${field.path}`} {...props} />
    if (field.type === 'video-editor') return <VideoPanel key={`${itemId}:${field.path}`} {...props} />
    return <V2FieldControl key={`${itemId}:${field.path}`} {...props} />
  }

  return (
    <section className={classes.card} aria-label={title}>
      <div className={classes.contentHead}>
        <h2 className={classes.title}>{title}</h2>
        <SlideTabs
          items={items}
          selected={index}
          onSelect={onSelect}
          onMove={onReorder}
          onAdd={onAdd}
          canAdd={canAdd}
          hint={ui.reorderHint}
          identity={(entry, position) => getAtPath(entry, identityPath) || `slide-${position}`}
        />
      </div>
      <div className={classes.contentBody}>
        {main.map(render)}
        <ExtraElements
          key={itemId}
          sectionUi={ui}
          basePath={basePath}
          item={item}
          fields={fields}
          shared={shared}
          errors={errors}
          openElement={openElement}
          onOpen={setOpenElement}
          modes={elementModes}
          onMode={(id, mode) => setElementModes((current) => ({ ...current, [id]: mode }))}
        />
        {canRemove && (
          <div className={classes.deleteRow}>
            <p className={classes.deleteText}>
              {ui.deleteConfirm || t('editorV2.form.slides.deleteConfirm')}
            </p>
            <button type="button" className={classes.redButton} onClick={() => onRemove(index)}>
              {ui.deleteLabel || t('editorV2.form.slides.deleteSlide')}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
